import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DoneIcon from '@mui/icons-material/Done';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import { adminService } from '../../services/api';

const statusColor = (s) => {
  switch (s) {
    case 'new': return 'primary';
    case 'in_progress': return 'warning';
    case 'resolved': return 'success';
    default: return 'default';
  }
};

// Timestamp formatter (Firestore Timestamp or plain object)
const formatTs = (ts) => {
  try {
    let dateObj = null;
    if (ts?.toDate) {
      const val = ts.toDate();
      dateObj = val instanceof Date ? val : new Date(val);
    } else if (ts?.seconds) {
      dateObj = new Date(ts.seconds * 1000);
    } else if (typeof ts === 'string' || typeof ts === 'number') {
      dateObj = new Date(ts);
    }
    if (!dateObj || isNaN(dateObj.getTime())) return '-';
    const d = dateObj.toLocaleDateString(undefined, { year:'numeric', month:'short', day:'numeric' });
    const t = dateObj.toLocaleTimeString(undefined, { hour:'2-digit', minute:'2-digit' });
    return <span title={dateObj.toLocaleString()}>{d} {t}</span>;
  } catch { return '-'; }
};

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await adminService.getContactMessages(filter);
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line
  }, [filter]);

  const openMessage = (m) => {
    setSelected(m);
    setAdminNote(m.adminNote || '');
    setOpen(true);
  };

  const handleStatus = async (status) => {
    if (!selected) return;
    try {
      await adminService.updateContactMessage(selected.id, { status, adminNote });
      toast.success('Updated');
      setOpen(false);
      load();
    } catch (e) {
      console.error(e);
      toast.error('Update failed');
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={600}>Contact Messages</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            select
            SelectProps={{ native: true }}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.map(m => (
              <TableRow key={m.id} hover>
                <TableCell>{m.name}</TableCell>
                <TableCell>{m.email}</TableCell>
                <TableCell>{m.subject || '-'} </TableCell>
                <TableCell><Chip size="small" label={m.status} color={statusColor(m.status)} /></TableCell>
                <TableCell>
                  {formatTs(m.createdAt)}
                </TableCell>
                <TableCell>
                  {formatTs(m.updatedAt)}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => openMessage(m)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {m.status !== 'resolved' && (
                    <Tooltip title="Mark Resolved">
                      <IconButton size="small" onClick={async () => { setSelected(m); setAdminNote(m.adminNote || ''); await handleStatus('resolved'); }}>
                        <DoneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {messages.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>No messages found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Message Details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>From</Typography>
              <Typography variant="body2" gutterBottom>{selected.name} &lt;{selected.email}&gt;</Typography>
              <Typography variant="subtitle2" gutterBottom mt={2}>Subject</Typography>
              <Typography variant="body2" gutterBottom>{selected.subject || '-'}</Typography>
              <Typography variant="subtitle2" gutterBottom mt={2}>Message</Typography>
              <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>{selected.message}</Typography>
              <Typography variant="subtitle2" gutterBottom mt={2}>Admin Note</Typography>
              <TextField fullWidth multiline minRows={3} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {selected && selected.status !== 'resolved' && (
            <Button variant="contained" onClick={() => handleStatus('resolved')} startIcon={<DoneIcon />}>Mark Resolved</Button>
          )}
          {selected && selected.status === 'new' && (
            <Button onClick={() => handleStatus('in_progress')}>In Progress</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMessages;
